import React, {useEffect, useState, useMemo, useRef} from "react";
import {Row, Col, Card, Button, Spin, Tag, Typography, Input, message} from "antd";
import {useAuth} from "../hooks/useAuth";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate, useLocation } from "react-router-dom";
import { OnboardingWizard } from "../components/OnboardingWizard";
import { SimpleOnboardingWizard } from "../components/SimpleOnboardingWizard";
import { SimpleOnboardingWizardV2 } from "../components/SimpleOnboardingWizardV2";
import { useGetBrandsQuery, useCalculateBrandEquityMutation, useGetStaleCachedBrandEquityMutation, useGetBrandEquityTrendsQuery, useGetSurveysQuery, useGetSurveyResponseSummaryQuery, useGetAllDashboardInsightsWithDataMutation, useGetSingleDashboardInsightWithDataMutation, useGetAIExpertsQuery, useGetSurveyCohortsQuery, useGetSystemSettingQuery, useCompleteOnboardingMutation, useGetOrgBrandBenchmarksQuery, useGetOrbitalItemsQuery, useGetBuildingBlocksInsightsMutation, useGetAiPublishedVersionsQuery } from "../services/nestjs-base-api";
import { useSubscriptionFeatures } from "../hooks/useSubscriptionFeatures";
import { ExperimentOutlined, BookOutlined, MessageOutlined, BarChartOutlined, LineChartOutlined, LoadingOutlined, ReloadOutlined, ShareAltOutlined, CopyOutlined, SendOutlined, LinkOutlined, WarningOutlined, PlusOutlined, DownOutlined, RightOutlined, BulbOutlined, LockOutlined } from "@ant-design/icons";
import { IBrandEquityScoreResponse, PeriodEnum } from "../services/nestjs-base-api/types";
import '../styles/revolutionary-dashboard.css';
import { Utils } from '../utils/utils';
import { renderFlagIcon, getLanguageName } from '../constants/countries';
import MiniOrbitalHero from '../components/BrandEquity/MiniOrbitalHero';
import DashboardEmptyState from '../components/Dashboard/DashboardEmptyState';
import { getDisplayName } from '../utils/elementDisplayNames';
import { escapeHtml, sanitizeInlineHtml } from '../utils/sanitize';
import { MaturityAlert } from '../components/MaturityAlert';

const { Title, Text, Paragraph } = Typography;

// Module-level variable to track the last calculatedAt we fetched insights for
// This persists across component unmount/remount (navigation) to prevent unnecessary refetches
let lastFetchedInsightsCalculatedAt: string | null = null;

const DashboardPage: React.FunctionComponent = () => {
    const { user, logout } = useAuth();

    // Fetch onboarding version from system settings
    const { data: onboardingVersionData } = useGetSystemSettingQuery({
        jwtToken: user?.jwtToken || '',
        settingName: 'USE_ONBOARDING_V2'
    }, {
        skip: !user?.jwtToken
    });

    // Fetch building blocks visibility setting
    const { data: buildingBlocksEnabledData } = useGetSystemSettingQuery({
        jwtToken: user?.jwtToken || '',
        settingName: 'DASHBOARD_BUILDING_BLOCKS_ENABLED'
    }, {
        skip: !user?.jwtToken
    });
    const buildingBlocksEnabled = (buildingBlocksEnabledData as any) === true || buildingBlocksEnabledData === 'true';

    // Parse the setting value (defaults to true if not set)
    const USE_ONBOARDING_V2 = (onboardingVersionData as any) === true || onboardingVersionData === 'true' || onboardingVersionData === undefined;
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const [brandEquityData, setBrandEquityData] = useState<IBrandEquityScoreResponse | null>(null);

    // Active period state — synced from saved filters (default: 30 days)
    const [activePeriod, setActivePeriod] = useState<PeriodEnum>(PeriodEnum.THIRTY_DAYS);
    const [activePeriodType, setActivePeriodType] = useState<string>('last30');
    const [activeDateFrom, setActiveDateFrom] = useState<string | undefined>();
    const [activeDateTo, setActiveDateTo] = useState<string | undefined>();

    // Cache key for previous period score (includes period to avoid cross-period conflicts)
    const PREV_PERIOD_STORAGE_KEY = `prevPeriodScore_${user?.organisation?.id || 'default'}_${activePeriod}`;

    // Initialize from localStorage for instant display on navigation
    const [previousPeriodScore, setPreviousPeriodScore] = useState<number | null>(() => {
        const cached = localStorage.getItem(PREV_PERIOD_STORAGE_KEY);
        if (cached) {
            const parsed = JSON.parse(cached);
            // Check if cache is less than 1 hour old
            if (Date.now() - parsed.timestamp < 60 * 60 * 1000) {
                return parsed.score;
            }
        }
        return null;
    });
    const previousPeriodFetchingRef = useRef(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [brandEquityLoadAttempted, setBrandEquityLoadAttempted] = useState(false);
    const [selectedStatementType, setSelectedStatementType] = useState('controversial');
    const [onboardingWizardStarted, setOnboardingWizardStarted] = useState(false);
    const [buildingBlocksExpanded, setBuildingBlocksExpanded] = useState(() => {
        const saved = localStorage.getItem('dashboard_building_blocks_expanded');
        return saved !== null ? saved === 'true' : true;
    });

    // Helper: map frontend periodType to PeriodEnum (same mapping as DataSelectionPage)
    const periodTypeToPeriodEnum = (periodType: string): PeriodEnum => {
        switch (periodType) {
            case 'last30': return PeriodEnum.THIRTY_DAYS;
            case 'last60': return PeriodEnum.SIXTY_DAYS;
            case 'last90': return PeriodEnum.NINETY_DAYS;
            case 'last120': return PeriodEnum.ONE_TWENTY_DAYS;
            case 'last180': return PeriodEnum.ONE_EIGHTY_DAYS;
            case 'last365': return PeriodEnum.THREE_SIXTY_FIVE_DAYS;
            case 'allTime': return PeriodEnum.ALL_TIME;
            default: return PeriodEnum.THIRTY_DAYS;
        }
    };

    // Helper: human-readable label for the active period
    const getPeriodLabel = (periodType: string, dateFrom?: string, dateTo?: string): string => {
        const labels: Record<string, string> = {
            'last30': 'Last 30 days', 'last60': 'Last 60 days',
            'last90': 'Last 90 days', 'last120': 'Last 120 days',
            'last180': 'Last 180 days', 'last365': 'Last 365 days',
            'allTime': 'All time',
        };
        if (periodType === 'custom' && dateFrom && dateTo) return `${dateFrom} — ${dateTo}`;
        return labels[periodType] || 'Last 30 days';
    };

    // Helper: calculate previous period date range for comparison
    // Returns null for ALL_TIME and custom DATE_RANGE (no meaningful comparison)
    const getPreviousPeriodRange = (period: PeriodEnum, dateFrom?: string, dateTo?: string): { dateFrom: string; dateTo: string } | null => {
        const periodDaysMap: Record<string, number> = {
            [PeriodEnum.THIRTY_DAYS]: 30,
            [PeriodEnum.SIXTY_DAYS]: 60,
            [PeriodEnum.NINETY_DAYS]: 90,
            [PeriodEnum.ONE_TWENTY_DAYS]: 120,
            [PeriodEnum.ONE_EIGHTY_DAYS]: 180,
            [PeriodEnum.THREE_SIXTY_FIVE_DAYS]: 365,
        };

        if (period === PeriodEnum.ALL_TIME || period === PeriodEnum.DATE_RANGE) {
            return null;
        }

        const days = periodDaysMap[period];
        if (!days) return null;

        const now = new Date();
        const prevTo = new Date(now);
        prevTo.setDate(prevTo.getDate() - (days + 1));
        const prevFrom = new Date(now);
        prevFrom.setDate(prevFrom.getDate() - (days * 2));

        return {
            dateFrom: prevFrom.toISOString().split('T')[0],
            dateTo: prevTo.toISOString().split('T')[0],
        };
    };

    // Memoized ReactMarkdown components to prevent constant re-renders
    // This fixes the issue where link nodes get unmounted/remounted on every render
    const overviewMarkdownComponents = useMemo(() => ({
        p: ({children}: {children?: React.ReactNode}) => {
            const text = String(children);
            const isBullet = text.startsWith('•');
            return <p style={{
                margin: 0,
                lineHeight: '1.6',
                color: '#78350F',
                fontSize: '15px',
                paddingLeft: isBullet ? '12px' : 0,
                textIndent: isBullet ? '-12px' : 0
            }}>{children}</p>;
        },
        strong: ({children}: {children?: React.ReactNode}) => <strong style={{ fontWeight: 600, color: '#92400E' }}>{children}</strong>,
        a: ({href, title, children}: {href?: string, title?: string, children?: React.ReactNode}) => (
            <a
                href={href}
                title={title || (href?.includes('/ai-experts') ? 'Ask an AI Expert about this' : undefined)}
                onClick={(e) => {
                    if (href?.startsWith('/')) {
                        e.preventDefault();
                        navigate(href);
                    }
                }}
                style={{ color: '#B45309', cursor: 'pointer' }}
            >
                {children}
            </a>
        )
    }), [navigate]);

    // Memoized ReactMarkdown components for expert cards (dark theme)
    const expertMarkdownComponents = useMemo(() => ({
        p: ({children}: {children?: React.ReactNode}) => <span>{children}</span>,
        strong: ({children}: {children?: React.ReactNode}) => <strong style={{ fontWeight: 600, color: '#1f2937' }}>{children}</strong>,
        a: ({href, title, children}: {href?: string, title?: string, children?: React.ReactNode}) => (
            <a
                href={href}
                title={title || (href?.includes('/ai-experts') ? 'Ask an AI Expert about this' : undefined)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (href?.startsWith('/')) {
                        e.preventDefault();
                        navigate(href);
                    }
                }}
                style={{ color: '#F59E0B', cursor: 'pointer' }}
            >
                {children}
            </a>
        )
    }), [navigate]);

    // Mutation for auto-completing onboarding
    const [completeOnboarding] = useCompleteOnboardingMutation();

    // Color scheme function matching statements sentiment page
    const getScoreColor = (value: number, metricType: string) => {
        const absValue = Math.abs(value);
        
        switch (metricType) {
            case 'controversial':
                // Controversy: Inverted scale - lower is better (green to red)
                if (absValue <= 20) return '#52c41a'; // Dark Green
                if (absValue <= 40) return '#a0d911'; // Light Green  
                if (absValue <= 60) return '#fadb14'; // Yellow
                if (absValue <= 80) return '#fa8c16'; // Orange
                return '#f5222d'; // Red
                
            case 'powerful':
            case 'highest': 
            case 'passionate':
                // Power, Score, Intensity: Regular scale - higher is better (red to green)
                if (value < 0) return '#000000'; // Black for negative
                if (absValue <= 20) return '#f5222d'; // Red
                if (absValue <= 40) return '#fa8c16'; // Orange
                if (absValue <= 60) return '#fadb14'; // Yellow
                if (absValue <= 80) return '#a0d911'; // Light Green
                return '#52c41a'; // Dark Green
                
            default:
                return '#1890ff'; // Default blue
        }
    };

    const getMetricColor = (percentage: number, type: 'polarity' | 'intensity' | 'power' | 'controversy') => {
        if (type === 'controversy') {
            // Controversy: lower is better
            if (percentage <= 20) return '#237804'; // Dark green
            if (percentage <= 40) return '#52c41a'; // Light green
            if (percentage <= 60) return '#faad14'; // Yellow
            if (percentage <= 80) return '#fa8c16'; // Orange
            return '#f5222d'; // Red for 81-100
        }
        
        // Standardized colors for Score, Intensity, and Brand Power
        if (percentage < 0) return '#000000'; // Black for negative values (only Score)
        if (percentage <= 20) return '#f5222d'; // Red
        if (percentage <= 40) return '#fa8c16'; // Orange
        if (percentage <= 60) return '#faad14'; // Yellow
        if (percentage <= 80) return '#52c41a'; // Light green
        return '#237804'; // Dark green for 81-100
    };
    
    const getPolarityColor = (percentage: number) => {
        // Use standardized color scheme for polarity
        if (percentage < 0) return '#000000'; // Black for negative values
        if (percentage === 0) return '#f5222d'; // Red for 0
        if (percentage <= 20) return '#f5222d'; // Red
        if (percentage <= 40) return '#fa8c16'; // Orange
        if (percentage <= 60) return '#faad14'; // Yellow
        if (percentage <= 80) return '#52c41a'; // Light green
        return '#237804'; // Dark green for 81-100
    };
    
    // Use the same localStorage keys as BrandEquityCalculationPage
    const FILTERS_STORAGE_KEY = `brandEquityFilters_${user?.id || 'default'}`;
    const RESULTS_STORAGE_KEY = `brandEquityResults_${user?.id || 'default'}`;

    const error: any = null;

    // Get brands to check bypass status
    const { data: brandsData } = useGetBrandsQuery({
        jwtToken: user?.jwtToken || '',
        organisationId: user?.organisation?.id || 0,
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Get surveys to check if organization has received any responses
    const { data: surveysData, isLoading: surveysLoading } = useGetSurveysQuery({
        jwtToken: user?.jwtToken || '',
        organisationId: user?.organisation?.id || 0,
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Get survey cohorts for displaying cohort names
    const { data: surveyCohorts = [] } = useGetSurveyCohortsQuery({
        jwtToken: user?.jwtToken || '',
        organisationId: user?.organisation?.id || 0,
    }, {
        skip: !user?.jwtToken || !user?.organisation?.id
    });

    // Helper function to get cohort display name
    const getCohortDisplayName = (cohortId: number): string => {
        const cohort = surveyCohorts.find(c => c.id === cohortId);
        if (!cohort) return 'Unknown Cohort';

        // Show "All Customers" instead of "Random Customers"
        if (cohort.code === 'random_customers') {
            return 'All Customers';
        }

        return cohort.name;
    };

    const getCohortDescription = (cohortId: number): string => {
        const cohort = surveyCohorts.find(c => c.id === cohortId);
        return cohort?.description || '';
    };

    // Get system setting for showing missing cohort surveys alert
    const { data: showMissingCohortAlert } = useGetSystemSettingQuery({
        jwtToken: user?.jwtToken || '',
        settingName: 'SHOW_MISSING_COHORT_SURVEYS_ALERT'
    }, {
        skip: !user?.jwtToken
    });

    // Fetch element descriptions for mini orbital tooltips
    const { data: elementDescriptionsData } = useGetSystemSettingQuery({
        jwtToken: user?.jwtToken || '',
        settingName: 'ELEMENT_DESCRIPTIONS'
    }, {
        skip: !user?.jwtToken
    });

    const elementDescriptions = useMemo(() => {
        try {
            if (elementDescriptionsData) {
                let parsed;
                if (typeof elementDescriptionsData === 'string') {
                    try {
                        parsed = JSON.parse(elementDescriptionsData);
                        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
                    } catch { return {}; }
                } else if (typeof elementDescriptionsData === 'object') {
                    parsed = elementDescriptionsData;
                } else {
                    return {};
                }
                if (parsed && typeof parsed === 'object') {
                    if (parsed.elements && typeof parsed.elements === 'object') return parsed.elements;
                    if (parsed.value) {
                        try {
                            const valueData = typeof parsed.value === 'string' ? JSON.parse(parsed.value) : parsed.value;
                            return valueData.elements || {};
                        } catch { /* ignore */ }
                    }
                    const firstKey = Object.keys(parsed)[0];
                    if (firstKey && parsed[firstKey] && (parsed[firstKey].mouseover !== undefined || parsed[firstKey].sidebar !== undefined)) {
                        return parsed;
                    }
                }
                return {};
            }
        } catch { /* ignore */ }
        return {};
    }, [elementDescriptionsData]);

    // Use the first brand to check bypass status and get brand equity data
    const firstBrandId = brandsData?.length ? brandsData[0].id : undefined;
    const firstBrand = brandsData?.[0];

    // Get response summary for the first survey to check if there are any responses
    const firstSurveyId = surveysData?.[0]?.id;
    const { data: responseSummary, isLoading: responseSummaryLoading } = useGetSurveyResponseSummaryQuery({
        jwtToken: user?.jwtToken || '',
        surveyId: firstSurveyId || 0,
    }, {
        skip: !user?.jwtToken || !firstSurveyId
    });

    // Calculate total responses across all surveys
    const totalResponsesAcrossAllSurveys = React.useMemo(() => {
        // If we have brandEquityData, use its totalResponses as it counts across all surveys
        if (brandEquityData) return brandEquityData.totalResponses;

        // Otherwise, sum up accessCount from all surveys for the first brand
        if (surveysData && surveysData.length > 0 && firstBrand) {
            return surveysData
                .filter(survey => {
                    const surveyBrandId = survey.brandId || survey.brand?.id;
                    return surveyBrandId === firstBrand.id;
                })
                .reduce((total, survey) => total + (survey.accessCount || 0), 0);
        }

        // Fall back to first survey's response summary if nothing else is available
        return responseSummary?.totalResponses || 0;
    }, [brandEquityData, surveysData, firstBrand, responseSummary]);
    const { bypassEnabled } = useSubscriptionFeatures(firstBrandId);

    // Fetch benchmark data for comparison on the dashboard card (uses active period)
    const { data: benchmarkData } = useGetOrgBrandBenchmarksQuery({
        jwtToken: user?.jwtToken || '',
        brandId: firstBrand?.id || 0,
        query: {
            timePeriod: activePeriod === PeriodEnum.DATE_RANGE ? '30_days' : activePeriod,
            productType: 'all',
            includeHistorical: false
        }
    }, { skip: !firstBrand?.id || !user?.jwtToken });

    // Orbital AI items (findings & actions flanking the orbital)
    const { data: orbitalDataRaw, isLoading: orbitalLoading } = useGetOrbitalItemsQuery({
        jwtToken: user?.jwtToken || '',
        period: activePeriod,
    }, { skip: !user?.jwtToken || !brandEquityData });
    const orbitalData = orbitalDataRaw as { enabled: boolean; orbital_findings: { items: string[] | null; reason?: string; cached?: boolean }; orbital_actions: { items: string[] | null; reason?: string; cached?: boolean } } | undefined;

    // Building Blocks AI insights (cached in localStorage to persist across navigation).
    // Validity check matches both the brand-equity calculation timestamp AND the
    // building_blocks_ai subsystem's currently-published version, so an admin Publish
    // automatically busts every user's localStorage cache on next load.
    const [getBuildingBlocksInsights] = useGetBuildingBlocksInsightsMutation();
    const { data: aiPublishedVersions } = useGetAiPublishedVersionsQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !user?.jwtToken },
    );
    const buildingBlocksAiVersion = aiPublishedVersions?.building_blocks_ai ?? null;
    const BUILDING_BLOCKS_STORAGE_KEY = `dashboard-building-blocks-${user?.organisation?.id}`;
    const [buildingBlocksInsights, setBuildingBlocksInsights] = useState<Record<string, { response: string | null; cached: boolean; elementKey: string }> | null>(() => {
        const saved = localStorage.getItem(BUILDING_BLOCKS_STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.insights || null;
            } catch {
                return null;
            }
        }
        return null;
    });
    const [buildingBlocksLoading, setBuildingBlocksLoading] = useState(false);

    useEffect(() => {
        if (!user?.jwtToken || !brandEquityData || !firstBrand?.id || !buildingBlocksEnabled) return;
        if (brandEquityData.insufficientSampleSize) return;
        if (!brandEquityData.latentElementScores?.length) return;
        // Wait until we know the published version; otherwise we can't decide
        // whether the localStorage cache is still valid.
        if (buildingBlocksAiVersion === undefined) return;

        // Skip fetch if we already have cached insights for this calculation AND
        // the cache was written against the currently-published prompt version.
        const saved = localStorage.getItem(BUILDING_BLOCKS_STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (
                    parsed.calculatedAt === brandEquityData.calculatedAt &&
                    parsed.bbAiVersion === buildingBlocksAiVersion
                ) {
                    if (!buildingBlocksInsights) setBuildingBlocksInsights(parsed.insights);
                    return;
                }
            } catch {
                // Continue to fetch
            }
        }

        const fetchBuildingBlocksInsights = async () => {
            try {
                setBuildingBlocksInsights(null);
                setBuildingBlocksLoading(true);
                const result = await getBuildingBlocksInsights({
                    jwtToken: user.jwtToken,
                    brandEquityData,
                    brandId: firstBrand.id,
                }).unwrap();
                setBuildingBlocksInsights(result.insights);
                localStorage.setItem(BUILDING_BLOCKS_STORAGE_KEY, JSON.stringify({
                    insights: result.insights,
                    calculatedAt: brandEquityData.calculatedAt,
                    bbAiVersion: buildingBlocksAiVersion,
                }));
            } catch (err) {
                console.error('Failed to fetch building blocks insights:', err);
            } finally {
                setBuildingBlocksLoading(false);
            }
        };

        fetchBuildingBlocksInsights();
    }, [user?.jwtToken, brandEquityData, firstBrand?.id, buildingBlocksEnabled, buildingBlocksAiVersion]);

    // Calculate benchmark comparison for dashboard card
    const benchmarkComparison = useMemo(() => {
        if (!benchmarkData?.available || !benchmarkData?.data?.brandPerformance?.metrics) {
            return null;
        }

        // Find the level 0 (Overall) metric which contains industry mean
        const overallMetric = benchmarkData.data.brandPerformance.metrics.find((m: { level: number }) => m.level === 0);
        if (!overallMetric || overallMetric.industryMean === undefined) {
            return null;
        }

        return {
            industryMean: overallMetric.industryMean,
            sampleSize: benchmarkData.data.distribution?.sampleSize
        };
    }, [benchmarkData]);

    // Check if organization has any active surveys
    // Onboarding is about setup (creating surveys), not responses
    // If surveys are already created, the org is "set up" regardless of response count
    const hasActiveSurveys = surveysData && surveysData.length > 0 && surveysData.some(s => s.status === 'active');

    // Determine if wizard should be shown based on user needs and survey existence
    // Show wizard only if user needs onboarding AND organization has no active surveys
    // Wait for surveys to finish loading to avoid showing wizard when surveys exist
    // Also show wizard for agency users viewing a client org with no surveys
    const isAgencyViewingClient = !!(user?.agencyContext);
    const shouldShowWizard = (user?.needsOnboarding || (isAgencyViewingClient && !hasActiveSurveys)) && !hasActiveSurveys && !surveysLoading;

    // Set the wizard started flag when wizard should show
    // This ensures wizard stays mounted during survey creation
    // IMPORTANT: This must be before any early returns to follow React hooks rules
    useEffect(() => {
        if (shouldShowWizard && !onboardingWizardStarted) {
            setOnboardingWizardStarted(true);
        }
        // NOTE: We do NOT reset onboardingWizardStarted to false here anymore
        // The flag should only be reset when the user explicitly completes onboarding via handleOnboardingComplete
        // This prevents the wizard from being hidden when surveys are created during the onboarding process
    }, [shouldShowWizard, onboardingWizardStarted]);

    // Auto-complete onboarding for users joining organizations with existing surveys
    // This handles the case where a new org admin is added to an organization that's already set up
    useEffect(() => {
        const autoCompleteOnboarding = async () => {
            // Only auto-complete if:
            // 1. User needs onboarding (has_completed_onboarding = false)
            // 2. Organization already has active surveys (org is set up)
            // 3. Surveys have finished loading (we have accurate data)
            // 4. Onboarding wizard is NOT actively running (don't interrupt active onboarding)
            if (user?.needsOnboarding && hasActiveSurveys && !surveysLoading && !onboardingWizardStarted && user?.jwtToken) {
                try {
                    console.log('🎓 Auto-completing onboarding for user joining existing organization');
                    await completeOnboarding({ jwtToken: user.jwtToken });

                    // Update local user state to reflect completion
                    if (user) {
                        const updatedUser = { ...user, needsOnboarding: false };
                        localStorage.setItem('user', JSON.stringify(updatedUser));
                    }
                } catch (error) {
                    console.error('Failed to auto-complete onboarding:', error);
                }
            }
        };

        autoCompleteOnboarding();
    }, [user?.needsOnboarding, hasActiveSurveys, surveysLoading, onboardingWizardStarted, user?.jwtToken, completeOnboarding, user]);

    // Calculate which cohorts are missing active surveys
    const missingCohorts = React.useMemo(() => {
        if (!surveyCohorts || !surveysData || !firstBrand) {
            return [];
        }

        // Get active cohorts only
        const activeCohorts = surveyCohorts.filter(c => c.isActive !== false);

        // Get active surveys for the first brand
        const brandActiveSurveys = surveysData.filter(survey => {
            const surveyBrandId = survey.brandId || survey.brand?.id;
            return surveyBrandId === firstBrand.id && survey.status === 'active';
        });

        // Get cohort IDs that already have active surveys
        const usedCohortIds = new Set(
            brandActiveSurveys
                .map(s => s.cohortId || s.cohort?.id)
                .filter(id => id !== undefined)
        );

        // Return cohorts that don't have active surveys
        return activeCohorts.filter(cohort => !usedCohortIds.has(cohort.id));
    }, [surveyCohorts, surveysData, firstBrand]);

    const [calculateBrandEquity] = useCalculateBrandEquityMutation();
    const [getStaleCachedBrandEquity] = useGetStaleCachedBrandEquityMutation();
    const [isRefreshingInBackground, setIsRefreshingInBackground] = useState(false);
    
    // Get trend data for the first brand - DISABLED: Trends feature coming soon
    const { data: trendsData } = useGetBrandEquityTrendsQuery({
        jwtToken: user?.jwtToken || '',
        brandHash: firstBrand?.hash || '',
        period: activePeriod
    }, {
        skip: true // Temporarily disabled - trends feature coming soon
    });

    // Get AI experts data
    const { data: aiExpertsData } = useGetAIExpertsQuery({
        jwtToken: user?.jwtToken || '',
    }, {
        skip: !user?.jwtToken
    });

    // Get dashboard AI insights with mutation
    const [getDashboardInsights, { data: dashboardInsights, isLoading: insightsLoading }] = useGetAllDashboardInsightsWithDataMutation();
    const [getSingleInsight] = useGetSingleDashboardInsightWithDataMutation();
    const [loadingExperts, setLoadingExperts] = useState<Set<string>>(new Set());
    const [updatedInsights, setUpdatedInsights] = useState<{ [expertType: string]: { response: string; cached: boolean } }>({});

    // Cache insights in localStorage to persist across navigation
    const INSIGHTS_STORAGE_KEY = `dashboard-insights-${user?.organisation?.id}`;
    const [cachedInsights, setCachedInsights] = useState<any>(null);
    const [shareSectionExpanded, setShareSectionExpanded] = useState(false);

    // Load cached insights on mount
    useEffect(() => {
        const saved = localStorage.getItem(INSIGHTS_STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setCachedInsights(parsed.insights);
                // Also restore the lastFetchedInsightsCalculatedAt to prevent unnecessary fetches
                if (parsed.calculatedAt) {
                    lastFetchedInsightsCalculatedAt = parsed.calculatedAt;
                }
            } catch (e) {
                console.error('Failed to parse cached insights', e);
            }
        }
    }, [INSIGHTS_STORAGE_KEY]);

    // Save insights to localStorage when they arrive or when individual experts are refreshed
    // NOTE: cachedInsights is intentionally excluded from deps to prevent infinite re-render loop
    // (this effect sets cachedInsights, so including it would cause a cycle)
    useEffect(() => {
        if (brandEquityData?.calculatedAt && (dashboardInsights || Object.keys(updatedInsights).length > 0)) {
            // Merge base insights with any individual refreshes
            const mergedInsights = { ...(dashboardInsights || cachedInsights), ...updatedInsights };
            if (Object.keys(mergedInsights).length > 0) {
                localStorage.setItem(INSIGHTS_STORAGE_KEY, JSON.stringify({
                    insights: mergedInsights,
                    calculatedAt: brandEquityData.calculatedAt
                }));
                setCachedInsights(mergedInsights);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboardInsights, updatedInsights, brandEquityData?.calculatedAt, INSIGHTS_STORAGE_KEY]);

    // Fetch dashboard insights when brand equity data is available
    useEffect(() => {
        if (user?.jwtToken && brandEquityData) {
            // Skip if insufficient sample size - not enough data for meaningful analysis
            if (brandEquityData.insufficientSampleSize) return;

            // Skip if we already fetched insights for this exact calculation (prevents refetch on navigation)
            const calculatedAt = brandEquityData.calculatedAt;
            if (lastFetchedInsightsCalculatedAt === calculatedAt) {
                return;
            }
            lastFetchedInsightsCalculatedAt = calculatedAt;

            // Clear any individual expert refreshes when brand equity data changes
            setUpdatedInsights({});
            getDashboardInsights({
                jwtToken: user.jwtToken,
                brandEquityData: brandEquityData,
                forceRefresh: false
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.jwtToken, brandEquityData]);

    // Function to refresh a single expert
    const refreshSingleExpert = async (expertType: string) => {
        if (!user?.jwtToken || !brandEquityData || !firstBrand?.id) return;

        // Skip if insufficient sample size - not enough data for meaningful analysis
        if (brandEquityData.insufficientSampleSize) return;
        
        setLoadingExperts(prev => new Set(Array.from(prev).concat(expertType)));
        
        try {
            let dataForInsight = brandEquityData;
            
            // For dashboard_overview, we need fresh brand equity data WITH AI summaries
            if (expertType === 'dashboard_overview') {
                // Mark dashboard_overview as used so future dashboard loads include AI summaries
                localStorage.setItem('dashboard_overview_used', 'true');

                // Determine which brand to use from saved filters
                const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
                let selectedBrandId = firstBrand.id;
                if (savedFilters) {
                    try {
                        const filters = JSON.parse(savedFilters);
                        // Use saved brand if it matches current org, otherwise use first brand
                        if (filters.brandId && brandsData?.some(b => b.id === filters.brandId)) {
                            selectedBrandId = filters.brandId;
                        }
                    } catch (e) {
                        console.error('Failed to parse saved filters, using first brand', e);
                    }
                }

                // Use active period from saved filters (synced with visualization)
                let payload: any = {
                    brandIds: [selectedBrandId],
                    period: activePeriod,
                    skipAiAnalysis: false, // Get AI summaries for dashboard overview
                    ...(activePeriod === PeriodEnum.DATE_RANGE && activeDateFrom && activeDateTo
                        ? { dateFrom: activeDateFrom, dateTo: activeDateTo }
                        : {})
                }
                
                // Get fresh brand equity data with AI summaries
                const freshBrandEquityResult = await calculateBrandEquity({
                    jwtToken: user.jwtToken,
                    organisationId: user.organisation?.id || 0,
                    payload: payload
                }).unwrap();
                dataForInsight = freshBrandEquityResult;
                
                // Don't update main brandEquityData to avoid affecting other experts
                // The enhanced data will be used for this specific dashboard_overview call
                // and cached separately without affecting other expert displays
            }
            
            const result = await getSingleInsight({
                jwtToken: user.jwtToken,
                expertType,
                brandEquityData: dataForInsight,
                forceRefresh: true
            }).unwrap();
            
            // Update the updatedInsights state with the new result
            setUpdatedInsights(prev => ({
                ...prev,
                [expertType]: result
            }));
        } catch (error) {
            console.error(`Error refreshing ${expertType}:`, error);
        } finally {
            setLoadingExperts(prev => {
                const newSet = new Set(Array.from(prev));
                newSet.delete(expertType);
                return newSet;
            });
        }
    };

    // Merge insights from initial load, cached insights, and individual refreshes
    // Use cachedInsights as fallback when dashboardInsights is not available (e.g., after navigation)
    const currentInsights = { ...(dashboardInsights || cachedInsights), ...updatedInsights };

    // Sync active period from saved filters on mount (independent of data loading)
    useEffect(() => {
        const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
        if (savedFilters) {
            try {
                const filters = JSON.parse(savedFilters);
                if (filters.periodType) {
                    setActivePeriodType(filters.periodType);
                    setActivePeriod(filters.period || periodTypeToPeriodEnum(filters.periodType));
                    setActiveDateFrom(filters.dateFrom);
                    setActiveDateTo(filters.dateTo);
                }
            } catch (e) {
                // Use defaults
            }
        }
    }, [FILTERS_STORAGE_KEY]);

    // Load saved brand equity data from localStorage on mount
    // Format: { data: IBrandEquityScoreResponse, timestamp: number } (shared with DataSelectionPage/VisualizationPage)
    useEffect(() => {
        const savedResults = localStorage.getItem(RESULTS_STORAGE_KEY);
        if (savedResults) {
            try {
                const parsed = JSON.parse(savedResults);
                // Support both formats: wrapped { data, timestamp } and legacy raw result
                const resultData = parsed.data || parsed;
                const timestamp = parsed.timestamp || (resultData.calculatedAt ? new Date(resultData.calculatedAt).getTime() : 0);
                // Check if data is recent (within last 24 hours)
                const hoursSinceCalculation = (Date.now() - timestamp) / (1000 * 60 * 60);

                if (hoursSinceCalculation < 24) {
                    setBrandEquityData(resultData);
                }
            } catch (e) {
                console.error('Failed to parse saved brand equity data', e);
            }
        }
    }, [RESULTS_STORAGE_KEY]);
    
    // Calculate brand equity data using stale-while-revalidate pattern
    // 1. Show stale/cached data immediately for instant UX
    // 2. Refresh in background if needed
    useEffect(() => {
        const loadBrandEquityData = async () => {
            // Skip if we already have data or no brand available
            if (brandEquityData || !user?.jwtToken || !user?.organisation?.id || !firstBrand?.id) {
                if (brandEquityData) setBrandEquityLoadAttempted(true);
                return;
            }

            // Load saved filters from BrandEquityCalculationPage (brand + period)
            const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);

            // Determine which brand and period to use from saved filters
            let selectedBrandId = firstBrand.id;
            let selectedPeriod: PeriodEnum = PeriodEnum.THIRTY_DAYS;
            let selectedPeriodType = 'last30';
            let selectedDateFrom: string | undefined;
            let selectedDateTo: string | undefined;

            if (savedFilters) {
                try {
                    const filters = JSON.parse(savedFilters);
                    // Use saved brand if it matches current org, otherwise use first brand
                    if (filters.brandId && brandsData?.some(b => b.id === filters.brandId)) {
                        selectedBrandId = filters.brandId;
                    }
                    // Use saved period (syncs dashboard with visualization)
                    if (filters.periodType) {
                        selectedPeriodType = filters.periodType;
                        selectedPeriod = filters.period || periodTypeToPeriodEnum(filters.periodType);
                        selectedDateFrom = filters.dateFrom;
                        selectedDateTo = filters.dateTo;
                    }
                } catch (e) {
                    console.error('Failed to parse saved filters, using defaults', e);
                }
            }

            // Update active period state for UI display and other effects
            setActivePeriod(selectedPeriod);
            setActivePeriodType(selectedPeriodType);
            setActiveDateFrom(selectedDateFrom);
            setActiveDateTo(selectedDateTo);

            // Ensure filters are saved so VisualizationPage can display brand name and period
            if (!savedFilters) {
                localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
                    brandId: selectedBrandId,
                    periodType: selectedPeriodType,
                    period: selectedPeriod,
                    dateFrom: selectedDateFrom,
                    dateTo: selectedDateTo,
                }));
            }

            // Build payload using user's selected period (synced with visualization)
            const payload: any = {
                brandIds: [selectedBrandId],
                period: selectedPeriod,
                skipAiAnalysis: false,
                ...(selectedPeriod === PeriodEnum.DATE_RANGE && selectedDateFrom && selectedDateTo
                    ? { dateFrom: selectedDateFrom, dateTo: selectedDateTo }
                    : {})
            };

            try {
                // Step 1: Try to get stale cached data first (instant response)
                const staleResult = await getStaleCachedBrandEquity({
                    jwtToken: user.jwtToken,
                    organisationId: user.organisation.id,
                    payload
                }).unwrap();

                if (staleResult && staleResult.overallBrandEquityScore !== undefined) {
                    // Show stale data immediately - user sees something right away!
                    setBrandEquityData(staleResult);

                    // If data is stale, refresh in background
                    if (staleResult.isStale) {
                        setIsRefreshingInBackground(true);

                        // Background refresh - don't await, let it complete async
                        calculateBrandEquity({
                            jwtToken: user.jwtToken,
                            organisationId: user.organisation.id,
                            payload
                        }).unwrap().then((freshResult) => {
                            setBrandEquityData(freshResult);
                            localStorage.setItem(RESULTS_STORAGE_KEY, JSON.stringify({ data: freshResult, timestamp: Date.now() }));
                        }).catch((err) => {
                            console.error('Background refresh failed:', err);
                        }).finally(() => {
                            setIsRefreshingInBackground(false);
                        });
                    } else {
                        // Fresh data from cache - save to localStorage
                        localStorage.setItem(RESULTS_STORAGE_KEY, JSON.stringify({ data: staleResult, timestamp: Date.now() }));
                    }
                    setBrandEquityLoadAttempted(true);
                    return; // We have data, exit early
                }
            } catch (staleError) {
                // No cached data available, will calculate fresh below
            }

            // Step 2: No cache available - calculate fresh (show loading spinner)
            try {
                setIsCalculating(true);

                const result = await calculateBrandEquity({
                    jwtToken: user.jwtToken,
                    organisationId: user.organisation.id,
                    payload
                }).unwrap();

                setBrandEquityData(result);
                localStorage.setItem(RESULTS_STORAGE_KEY, JSON.stringify({ data: result, timestamp: Date.now() }));
            } catch (error) {
                console.error('Failed to calculate brand equity:', error);
            } finally {
                setIsCalculating(false);
                setBrandEquityLoadAttempted(true);
            }
        };

        loadBrandEquityData();
    }, [user?.jwtToken, user?.organisation?.id, firstBrand?.id, brandsData, calculateBrandEquity, getStaleCachedBrandEquity, brandEquityData, FILTERS_STORAGE_KEY, RESULTS_STORAGE_KEY]);

    // Reset previous period score when active period changes
    useEffect(() => {
        setPreviousPeriodScore(null);
        previousPeriodFetchingRef.current = false;
    }, [activePeriod]);

    // Fetch previous period score for comparison (relative to active period)
    useEffect(() => {
        // Only fetch once we have the current period data
        if (!brandEquityData || brandEquityData.insufficientSampleSize) {
            return;
        }
        if (!user?.jwtToken || !user?.organisation?.id || !firstBrand?.id) {
            return;
        }
        // Don't refetch if we already have it or if fetch is in progress
        if (previousPeriodScore !== null || previousPeriodFetchingRef.current) {
            return;
        }

        // No previous period comparison for ALL_TIME or custom date ranges
        const prevRange = getPreviousPeriodRange(activePeriod, activeDateFrom, activeDateTo);
        if (!prevRange) {
            setPreviousPeriodScore(-999);
            return;
        }

        const fetchPreviousPeriod = async () => {
            // Mark as fetching to prevent duplicate requests
            previousPeriodFetchingRef.current = true;

            // Small delay to ensure first brand equity calculation completes
            await new Promise(resolve => setTimeout(resolve, 500));

            try {
                const result = await calculateBrandEquity({
                    jwtToken: user.jwtToken,
                    organisationId: user.organisation.id,
                    payload: {
                        brandIds: [firstBrand.id],
                        period: PeriodEnum.DATE_RANGE,
                        dateFrom: prevRange.dateFrom,
                        dateTo: prevRange.dateTo,
                        skipAiAnalysis: true
                    }
                }).unwrap();

                if (result && !result.insufficientSampleSize && result.overallBrandEquityScore !== undefined) {
                    setPreviousPeriodScore(result.overallBrandEquityScore);
                    // Cache in localStorage for instant display on navigation
                    localStorage.setItem(PREV_PERIOD_STORAGE_KEY, JSON.stringify({
                        score: result.overallBrandEquityScore,
                        timestamp: Date.now()
                    }));
                } else {
                    // Mark as attempted but no data available
                    setPreviousPeriodScore(-999);
                    localStorage.setItem(PREV_PERIOD_STORAGE_KEY, JSON.stringify({
                        score: -999,
                        timestamp: Date.now()
                    }));
                }
            } catch (error) {
                console.debug('Could not fetch previous period data:', error);
                setPreviousPeriodScore(-999);
            }
        };

        fetchPreviousPeriod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandEquityData?.calculatedAt, user?.jwtToken, user?.organisation?.id, firstBrand?.id, activePeriod]);

    const handleOnboardingComplete = () => {
        // Reset wizard started flag
        setOnboardingWizardStarted(false);

        // Update user state to remove needsOnboarding flag
        if (user) {
            const updatedUser = { ...user, needsOnboarding: false };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            // Force a small delay and page refresh to ensure clean state
            setTimeout(() => {
                window.location.reload();
            }, 100);
        }
    };
    
    const getScoreStatus = (score: number): { text: string, color: string } => {
        if (score >= 80) return { text: intl.formatMessage({ id: 'dashboard.score.revolutionary' }), color: '#52c41a' };
        if (score >= 70) return { text: intl.formatMessage({ id: 'dashboard.score.excellent' }), color: '#52c41a' };
        if (score >= 60) return { text: intl.formatMessage({ id: 'dashboard.score.strong' }), color: '#faad14' };
        if (score >= 40) return { text: intl.formatMessage({ id: 'dashboard.score.developing' }), color: '#fa8c16' };
        return { text: intl.formatMessage({ id: 'dashboard.score.emerging' }), color: '#f5222d' };
    };

    useEffect(() => {
        if (error) {
            if ('status' in error) {
                if (error.status === 401) {
                    logout();
                }
            }
        }
    }, [error, logout]);

    // Show loading spinner while user data is being loaded
    if (!user) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '400px' 
            }}>
                <Spin size="large" />
            </div>
        );
    }

    // Check if we're still loading data needed to determine onboarding status
    // IMPORTANT: Don't show loading if wizard has already started - let it stay mounted
    // Wait for surveys to load before making onboarding decision to avoid race conditions
    const isCheckingOnboardingStatus = user.needsOnboarding && !onboardingWizardStarted && surveysLoading;

    // Show loading while we're checking onboarding status to prevent flash
    if (isCheckingOnboardingStatus) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '400px' 
            }}>
                <Spin size="large" />
            </div>
        );
    }

    // Check for forced display via URL parameters (for testing/demo purposes)
    const searchParams = new URLSearchParams(location.search);
    const forceOnboarding = searchParams.get('onboarding');
    const forceShareSection = searchParams.get('share') === 'true';
    
    if (forceOnboarding === 'step1' || forceOnboarding === 'step2' || forceOnboarding === 'step3') {
        // Use environment variable to determine which wizard to show
        const useSimpleOnboarding = process.env.REACT_APP_USE_SIMPLE_ONBOARDING === 'true';

        // Map step1/step2/step3 to numeric indices
        const stepMap: Record<string, number> = {
            'step1': 0,
            'step2': 1,
            'step3': 2
        };
        const forceStep = stepMap[forceOnboarding] || 0;

        if (useSimpleOnboarding) {
            // Force specific step for SimpleOnboardingWizard
            if (USE_ONBOARDING_V2) {
                return <SimpleOnboardingWizardV2 onComplete={handleOnboardingComplete} forceStep={forceStep} />;
            }
            return <SimpleOnboardingWizard onComplete={handleOnboardingComplete} forceStep={forceStep} />;
        } else {
            return <OnboardingWizard onComplete={handleOnboardingComplete} />;
        }
    }

    // Show onboarding wizard if it has been started OR should be shown
    // Once started, the wizard stays mounted until completion to prevent state loss during survey creation
    if (onboardingWizardStarted || shouldShowWizard) {
        // Use environment variable to determine which wizard to show
        const useSimpleOnboarding = process.env.REACT_APP_USE_SIMPLE_ONBOARDING === 'true';

        if (useSimpleOnboarding) {
            if (USE_ONBOARDING_V2) {
                return <SimpleOnboardingWizardV2 onComplete={handleOnboardingComplete} />;
            }
            return <SimpleOnboardingWizard onComplete={handleOnboardingComplete} />;
        } else {
            return <OnboardingWizard onComplete={handleOnboardingComplete} />;
        }
    }


    return (
        <div className="revolutionary-dashboard gpu-accelerated" style={{ background: 'transparent' }}>

            <MaturityAlert type="beta" />

            {/* Share Survey Section - Only show surveys with 0 responses */}
            {(() => {
                // Filter surveys with no responses (accessCount === 0)
                const surveysWithNoResponses = surveysData?.filter(survey => (survey.accessCount === 0 || survey.accessCount === null || survey.accessCount === undefined)) || [];
                const shouldShow = forceShareSection || (surveysWithNoResponses.length > 0 && !responseSummaryLoading);

                return shouldShow && (
                <div style={{
                    background: '#ffffff',
                    borderRadius: '20px',
                    padding: shareSectionExpanded ? '32px' : '20px 32px',
                    marginBottom: '24px',
                    border: '2px solid #F59E0B',
                    boxShadow: '0 10px 40px rgba(245, 158, 11, 0.15)',
                    cursor: shareSectionExpanded ? 'default' : 'pointer',
                    transition: 'all 0.3s ease'
                }}
                onClick={() => !shareSectionExpanded && setShareSectionExpanded(true)}
                >
                    {/* Collapsed Header - Always visible */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setShareSectionExpanded(!shareSectionExpanded);
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <ShareAltOutlined style={{ fontSize: '24px', color: '#B45309' }} />
                            <Title level={3} style={{
                                color: '#B45309',
                                margin: 0,
                                fontSize: '22px',
                                fontWeight: '700'
                            }}>
                                Ready to Launch! Share Your {surveysWithNoResponses.length === 1 ? 'Survey' : 'Surveys'}
                            </Title>
                            <Tag color="orange" style={{ fontWeight: '600' }}>
                                {surveysWithNoResponses.length} {surveysWithNoResponses.length === 1 ? 'survey' : 'surveys'}
                            </Tag>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#92400E' }}>
                            <Text style={{ color: '#92400E', fontSize: '14px' }}>
                                {shareSectionExpanded ? 'Click to collapse' : 'Click to expand'}
                            </Text>
                            {shareSectionExpanded ? <DownOutlined /> : <RightOutlined />}
                        </div>
                    </div>

                    {/* Expanded Content */}
                    {shareSectionExpanded && (
                        <div style={{ marginTop: '24px' }}>
                            <Text style={{
                                color: '#78350F',
                                fontSize: '16px',
                                display: 'block',
                                marginBottom: '20px'
                            }}>
                                Your {surveysWithNoResponses.length === 1 ? 'survey is' : 'surveys are'} ready to collect insights. Share {surveysWithNoResponses.length === 1 ? 'this link' : 'these links'} to start measuring your brand equity.
                            </Text>

                            <Row gutter={[24, 24]} align="top">
                                <Col xs={24} md={16}>
                                    {/* Survey Links - Show surveys with no responses */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                        {(forceShareSection ? [{hash: 'demo-survey-hash', cohortId: 1}] : surveysWithNoResponses).map((survey, index) => {
                                            const cohortId = survey.cohortId || (survey as any).cohort?.id;
                                            return (
                                            <div key={index} style={{
                                                padding: '16px',
                                                backgroundColor: '#FFFBEB',
                                                borderRadius: 12,
                                                border: '1px solid #F59E0B'
                                            }}>
                                                <Text strong style={{
                                                    color: '#78350F',
                                                    fontSize: '14px',
                                                    display: 'block',
                                                    marginBottom: '8px'
                                                }}>
                                                    {getCohortDisplayName(cohortId)}
                                                </Text>
                                                <Text style={{
                                                    color: '#92400E',
                                                    fontSize: '12px',
                                                    display: 'block',
                                                    marginBottom: '12px'
                                                }}>
                                                    {getCohortDescription(cohortId)}
                                                </Text>
                                                {('countryCode' in survey && (survey.countryCode || survey.languageCode)) && (
                                                    <div style={{
                                                        display: 'flex',
                                                        gap: 8,
                                                        marginBottom: '12px',
                                                        flexWrap: 'wrap'
                                                    }}>
                                                        {survey.countryCode && (
                                                            <span style={{
                                                                lineHeight: 1
                                                            }}>
                                                                {renderFlagIcon(survey.countryCode, 28)}
                                                            </span>
                                                        )}
                                                        {survey.languageCode && (
                                                            <span style={{
                                                                fontSize: '12px',
                                                                color: '#78350F',
                                                                background: 'rgba(245, 158, 11, 0.15)',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                border: '1px solid rgba(245, 158, 11, 0.3)'
                                                            }}>
                                                                {getLanguageName(survey.languageCode)}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 12
                                                }}>
                                                    <LinkOutlined style={{ color: '#B45309', fontSize: '16px' }} />
                                                    <Input
                                                        value={Utils.getPublicSurveyUrl(survey.hash)}
                                                        readOnly
                                                        style={{
                                                            flex: 1,
                                                            fontFamily: 'monospace',
                                                            fontSize: '13px',
                                                            border: '1px solid #F59E0B',
                                                            background: 'white',
                                                            color: '#78350F',
                                                            padding: '8px 12px',
                                                            borderRadius: '6px'
                                                        }}
                                                    />
                                                    <Button
                                                        size="small"
                                                        icon={<CopyOutlined />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const publicUrl = Utils.getPublicSurveyUrl(survey.hash);
                                                            navigator.clipboard.writeText(publicUrl).then(() => {
                                                                message.success(`${getCohortDisplayName(cohortId)} survey link copied!`);
                                                            });
                                                        }}
                                                        style={{
                                                            background: '#F59E0B',
                                                            border: '1px solid #D97706',
                                                            color: 'white'
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                </Col>
                                <Col xs={24} md={8}>
                                    {/* Email Platform Tips */}
                                    <Card style={{
                                        background: '#FFFBEB',
                                        border: '1px solid #F59E0B',
                                        borderRadius: '16px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <SendOutlined style={{ color: '#B45309' }} />
                                            <Text strong style={{ color: '#78350F', fontSize: '16px' }}>
                                                Email Platform Tips
                                            </Text>
                                        </div>

                                        <Text style={{
                                            color: '#78350F',
                                            fontSize: '13px',
                                            marginBottom: '16px',
                                            display: 'block',
                                            fontWeight: '500'
                                        }}>
                                            Replace {'{'}customer_identifier{'}'} with the email address
                                        </Text>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{
                                                background: 'rgba(245, 158, 11, 0.1)',
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(245, 158, 11, 0.3)'
                                            }}>
                                                <Text strong style={{ color: '#92400E', fontSize: '13px', display: 'block' }}>
                                                    Klaviyo:
                                                </Text>
                                                <Text style={{ color: '#78350F', fontSize: '12px', fontFamily: 'monospace' }}>
                                                    Replace with {'{{'}email{'}}'} or {'{{'}contact.email{'}}'}
                                                </Text>
                                            </div>

                                            <div style={{
                                                background: 'rgba(245, 158, 11, 0.1)',
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(245, 158, 11, 0.3)'
                                            }}>
                                                <Text strong style={{ color: '#92400E', fontSize: '13px', display: 'block' }}>
                                                    Mailchimp:
                                                </Text>
                                                <Text style={{ color: '#78350F', fontSize: '12px', fontFamily: 'monospace' }}>
                                                    Replace with *|EMAIL|*
                                                </Text>
                                            </div>

                                            <div style={{
                                                background: 'rgba(245, 158, 11, 0.1)',
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: '1px solid rgba(245, 158, 11, 0.3)'
                                            }}>
                                                <Text strong style={{ color: '#92400E', fontSize: '13px', display: 'block' }}>
                                                    HubSpot:
                                                </Text>
                                                <Text style={{ color: '#78350F', fontSize: '12px', fontFamily: 'monospace' }}>
                                                    Replace with {'{{'}contact.email{'}}'}
                                                </Text>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            </Row>
                        </div>
                    )}
                </div>
                );
            })()}

            {/* Create Survey Section - Show if system setting is enabled and we have active surveys */}
            {((showMissingCohortAlert as any) === true || showMissingCohortAlert === 'true') && hasActiveSurveys && (
                <div style={{
                    background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #B45309 100%)',
                    borderRadius: '20px',
                    padding: '32px',
                    marginBottom: '24px',
                    boxShadow: '0 10px 40px rgba(59, 130, 246, 0.2)'
                }}>
                    <Row gutter={[24, 24]} align="middle">
                        <Col xs={24} md={18}>
                            <div>
                                <Title level={2} style={{
                                    color: 'white',
                                    margin: 0,
                                    marginBottom: '8px',
                                    fontSize: '28px',
                                    fontWeight: '700'
                                }}>
                                    {missingCohorts.length > 0 ? (
                                        <><WarningOutlined style={{ marginRight: '12px' }} />Missing Customer Cohort Surveys</>
                                    ) : (
                                        <><PlusOutlined style={{ marginRight: '12px' }} />Expand Your Survey Coverage</>
                                    )}
                                </Title>
                                <Text style={{
                                    color: 'rgba(255,255,255,0.9)',
                                    fontSize: '16px',
                                    display: 'block',
                                    marginBottom: '20px'
                                }}>
                                    {missingCohorts.length > 0
                                        ? "You haven't created active surveys for all customer cohorts. To get complete insights, create surveys for:"
                                        : "Create additional surveys for different cohorts or countries to maximize your brand insights across regions."}
                                </Text>

                                {/* Missing Cohort List - Only show if there are missing cohorts */}
                                {missingCohorts.length > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {missingCohorts.map((cohort, index) => (
                                            <div key={index} style={{
                                                padding: '16px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                backdropFilter: 'blur(10px)',
                                                borderRadius: 12,
                                                border: '1px solid rgba(255, 255, 255, 0.2)'
                                            }}>
                                                <Text strong style={{
                                                    color: 'rgba(255,255,255,0.95)',
                                                    fontSize: '16px',
                                                    display: 'block',
                                                    marginBottom: '4px'
                                                }}>
                                                    {getCohortDisplayName(cohort.id)}
                                                </Text>
                                                <Text style={{
                                                    color: 'rgba(255,255,255,0.8)',
                                                    fontSize: '13px',
                                                    display: 'block'
                                                }}>
                                                    {cohort.description}
                                                </Text>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Col>
                        <Col xs={24} md={6}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                alignItems: 'stretch'
                            }}>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<PlusOutlined />}
                                    onClick={() => navigate('/dashboard/surveys/create')}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.9)',
                                        border: '2px solid rgba(255, 255, 255, 1)',
                                        color: '#B45309',
                                        fontWeight: '600',
                                        height: '48px',
                                        borderRadius: '12px',
                                        width: '100%'
                                    }}
                                >
                                    Create Survey
                                </Button>
                                <Text style={{
                                    color: 'rgba(255,255,255,0.8)',
                                    fontSize: '12px',
                                    textAlign: 'center'
                                }}>
                                    {missingCohorts.length > 0
                                        ? "Create surveys for missing cohorts to maximize your brand insights"
                                        : "Expand to new countries or customer segments"}
                                </Text>
                            </div>
                        </Col>
                    </Row>
                </div>
            )}

            {/* Empty state - show when no brand equity data or insufficient sample */}
            {(!brandEquityData || brandEquityData.insufficientSampleSize) && !isCalculating && brandEquityLoadAttempted && (
                <DashboardEmptyState
                    brandName={firstBrand?.name || 'Brand'}
                    totalResponses={totalResponsesAcrossAllSurveys}
                    minimumResponses={10}
                    hasActiveSurveys={!!hasActiveSurveys}
                    surveys={surveysData?.filter(s => {
                        const surveyBrandId = s.brandId || s.brand?.id;
                        return surveyBrandId === firstBrand?.id;
                    }) || []}
                    onNavigateToSurveys={() => navigate('/dashboard/surveys')}
                    onNavigateToCreateSurvey={() => navigate('/dashboard/surveys/create')}
                    onNavigateToDataSelection={() => navigate('/dashboard/analytics/data-selection?returnTo=dashboard')}
                    periodLabel={getPeriodLabel(activePeriodType, activeDateFrom, activeDateTo)}
                />
            )}

            {/* Calculating state - matches MiniOrbitalHero container style */}
            {isCalculating && !brandEquityData && (
                <div style={{
                    background: 'linear-gradient(180deg, #f0f4f8 0%, #ffffff 40%)',
                    borderRadius: '20px',
                    padding: '28px 32px 24px',
                    marginBottom: '24px',
                    border: '2px solid #3d5a80',
                    boxShadow: '0 10px 40px rgba(61, 90, 128, 0.12), 0 1px 3px rgba(61, 90, 128, 0.08)',
                    position: 'relative' as const,
                    overflow: 'hidden',
                }}>
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 42, color: '#3d5a80' }} spin />} />
                        <Text style={{ display: 'block', marginTop: 16, color: '#2c4a6e', fontSize: 16 }}>
                            <FormattedMessage id="dashboard.emptyState.calculating" />
                        </Text>
                    </div>
                </div>
            )}

            {/* Mini Orbital Hero - with optional Findings & Actions flanking columns */}
            {brandEquityData && !brandEquityData.insufficientSampleSize && (() => {
                const orbitalEnabled = orbitalData?.enabled === true;
                const hasOrbitalItems = orbitalEnabled &&
                    orbitalData.orbital_findings?.items?.length;
                // Show skeletons while query is in flight (before we know enabled status)
                const showSkeletons = orbitalLoading && !orbitalData;
                const showFlanking = showSkeletons || hasOrbitalItems;

                const findingsItems = orbitalData?.orbital_findings?.items || [];

                const orbitalHero = (
                    <MiniOrbitalHero
                        brandEquityScore={brandEquityData.overallBrandEquityScore}
                        brandEquityClassificationLabel={brandEquityData.overallClassificationLabel}
                        brandLoyaltyScore={
                            brandEquityData.latentElementScores?.find(
                                (e) => e.element === 'Brand Loyalty' && !e.locked
                            )?.score ?? null
                        }
                        brandLoyaltyClassificationLabel={
                            brandEquityData.latentElementScores?.find(
                                (e) => e.element === 'Brand Loyalty' && !e.locked
                            )?.classificationLabel
                        }
                        perceivedQualityScore={
                            brandEquityData.latentElementScores?.find(
                                (e) => e.element === 'Perceived Quality' && !e.locked
                            )?.score ?? null
                        }
                        perceivedQualityClassificationLabel={
                            brandEquityData.latentElementScores?.find(
                                (e) => e.element === 'Perceived Quality' && !e.locked
                            )?.classificationLabel
                        }
                        totalResponses={brandEquityData.totalResponses}
                        previousPeriodScore={previousPeriodScore}
                        benchmarkComparison={benchmarkComparison}
                        isCalculating={isCalculating}
                        brandName={firstBrand?.name || 'Brand'}
                        elementDescriptions={elementDescriptions}
                        periodLabel={getPeriodLabel(activePeriodType, activeDateFrom, activeDateTo)}
                        onNavigateToDataSelection={() => navigate('/dashboard/analytics/data-selection?returnTo=dashboard')}
                        onNavigateToViz={() => navigate('/dashboard/analytics/visualization')}
                        onNavigateToTrends={() => navigate('/dashboard/analytics/trends')}
                        onNavigateToBenchmarking={() => navigate('/dashboard/benchmarking')}
                    />
                );

                return (
                <div style={{ position: 'relative' }}>
                    {/* Skeleton layer — fades out when resolved */}
                    <div className={showSkeletons ? 'orbital-skeletons-visible' : 'orbital-skeletons-hidden'}>
                        <Row gutter={[20, 16]} align="stretch">
                            <Col xs={24} lg={14}>{orbitalHero}</Col>
                            <Col xs={24} lg={10} style={{ display: 'flex' }}>
                                <div style={{ paddingTop: 8, paddingBottom: 24, display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: '#e07a5f', marginBottom: 10, textTransform: 'uppercase' as const }}>
                                        <BulbOutlined style={{ marginRight: 6 }} />
                                        Live AI Hot Take: Notable Findings
                                    </div>
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className="orbital-skeleton-pulse" style={{ borderLeft: '3px solid rgba(224, 122, 95, 0.2)', borderRadius: 8, minHeight: 70, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', marginBottom: i < 2 ? 10 : 0, padding: '12px 16px' }}>
                                            <div style={{ height: 10, width: '85%', borderRadius: 4, background: 'rgba(224, 122, 95, 0.10)', marginBottom: 8 }} />
                                            <div style={{ height: 10, width: '60%', borderRadius: 4, background: 'rgba(224, 122, 95, 0.07)' }} />
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    {/* Populated items layer — fades in when items arrive */}
                    {hasOrbitalItems && (
                        <div className="orbital-fade-in">
                            <Row gutter={[20, 16]} align="stretch">
                                <Col xs={24} lg={14}>{orbitalHero}</Col>
                                <Col xs={24} lg={10} style={{ display: 'flex' }}>
                                    <div style={{ paddingTop: 8, paddingBottom: 24, display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: '#e07a5f', marginBottom: 10, textTransform: 'uppercase' as const }}>
                                            <BulbOutlined style={{ marginRight: 6 }} />
                                            Live AI Hot Take: Notable Findings
                                        </div>
                                        {findingsItems.map((text, i) => (
                                            <div
                                                key={i}
                                                onClick={() => navigate('/dashboard/advice?tab=key-findings')}
                                                style={{ borderLeft: '3px solid #e07a5f', borderRadius: 8, background: 'rgba(224, 122, 95, 0.04)', minHeight: 50, flex: 1, display: 'flex', alignItems: 'center', marginBottom: 8, padding: '10px 16px', cursor: 'pointer', transition: 'background 0.2s' }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(224, 122, 95, 0.10)'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(224, 122, 95, 0.04)'}
                                            >
                                                <span style={{ color: '#1b2a41', fontSize: 15, fontWeight: 500, lineHeight: 1.5 }}>{text}</span>
                                            </div>
                                        ))}
                                        <div
                                            onClick={() => navigate('/dashboard/advice#tabs')}
                                            style={{ borderRadius: 8, background: '#e07a5f', border: '1px solid #c9664a', padding: '10px 16px', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center', marginTop: 'auto' }}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = '#c9664a'; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = '#e07a5f'; }}
                                        >
                                            <span style={{ color: '#fff', fontSize: 15, fontWeight: 500, lineHeight: 1.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                <FormattedMessage id="dashboard.orbital.seeMoreFindings" />
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 4 }}>
                                                    {[0, 1, 2].map(i => (
                                                        <span key={i} style={{ display: 'inline-block', fontSize: 18, fontWeight: 700, lineHeight: 1, opacity: 0.4 + i * 0.3, color: '#fff', animation: `ctaArrowRight 1.5s ease-in-out ${i * 0.15}s infinite` }}>&#x203A;</span>
                                                    ))}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {/* Full-width orbital — fades in when disabled (no flanking) */}
                    {!showSkeletons && !hasOrbitalItems && (
                        <div className="orbital-fade-in">
                            {orbitalHero}
                        </div>
                    )}
                </div>
                );
            })()}

            {/* Latent Elements Building Blocks - controlled by system setting */}
            {buildingBlocksEnabled && brandEquityData && !brandEquityData.insufficientSampleSize && brandEquityData.latentElementScores?.length > 0 && (() => {
                // Ring hierarchy order: Ring 1 first, then Ring 2, then Ring 3
                const ringOrder: Record<string, number> = {
                    'Brand Loyalty': 1,
                    'Perceived Quality': 1,
                    'Behavioural Loyalty': 2,
                    'Market Awareness': 2,
                    'Associations': 2,
                    'Attitudinal Loyalty': 2,
                    'Alignment Depth': 3,
                    'Willingness to Pay a Premium': 2,
                    'Brand Attachment': 3,
                    'Quality Value Tradeoff (PQV)': 3,
                };

                // Core questions for each element
                const coreQuestions: Record<string, string> = {
                    'Willingness to Pay a Premium': 'Could the brand charge more?',
                    'Brand Attachment': 'Are customers emotionally attached to the brand?',
                    'Quality Value Tradeoff (PQV)': 'Does the brand feel worth its price?',
                    'Alignment Depth': 'Do brand touchpoints deliver a great experience?',
                    'Behavioural Loyalty': 'Are customers likely to buy again?',
                    'Attitudinal Loyalty': 'Are customers proud to choose this brand?',
                    'Market Awareness': 'Is the brand front of mind?',
                    'Associations': 'Does the brand stand out in a positive way?',
                };

                const elements = brandEquityData.latentElementScores
                    .filter(e => e.element !== 'Brand Equity' && e.element !== 'Brand Loyalty' && e.element !== 'Perceived Quality')
                    .sort((a, b) => (ringOrder[a.element] || 99) - (ringOrder[b.element] || 99));

                if (elements.length === 0) return null;

                const getScoreBadgeColor = (score: number) => {
                    const pct = Math.abs(score);
                    if (pct > 40) return { bg: '#ecfdf5', text: '#059669', border: '#a7f3d0' };
                    if (pct > 20) return { bg: '#fffbeb', text: '#d97706', border: '#fde68a' };
                    return { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' };
                };

                return (
                    <div style={{ marginBottom: '24px' }}>
                        <div
                            onClick={() => {
                                const next = !buildingBlocksExpanded;
                                setBuildingBlocksExpanded(next);
                                localStorage.setItem('dashboard_building_blocks_expanded', String(next));
                            }}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                marginBottom: buildingBlocksExpanded ? '16px' : '0',
                                padding: '8px 0',
                            }}
                        >
                            <div style={{
                                flex: 1,
                                height: '1px',
                                background: '#e7e5e4',
                                maxWidth: '120px',
                            }} />
                            <span style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                letterSpacing: '0.1em',
                                color: '#a8a29e',
                                textTransform: 'uppercase' as const,
                                whiteSpace: 'nowrap' as const,
                            }}>
                                Building Blocks
                            </span>
                            <DownOutlined style={{
                                fontSize: '10px',
                                color: '#a8a29e',
                                transition: 'transform 0.3s ease',
                                transform: buildingBlocksExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            }} />
                            <div style={{
                                flex: 1,
                                height: '1px',
                                background: '#e7e5e4',
                                maxWidth: '120px',
                            }} />
                        </div>
                        <div style={{
                            maxHeight: buildingBlocksExpanded ? '2000px' : '0',
                            overflow: 'hidden',
                            transition: 'max-height 0.4s ease',
                        }}>
                        <Row gutter={[16, 14]}>
                            {elements.map((el) => {
                                const isLocked = el.locked;
                                const scoreBadge = !isLocked && el.score != null ? getScoreBadgeColor(el.score) : null;
                                const coreQuestion = coreQuestions[el.element] || '';
                                const bbInsight = buildingBlocksInsights?.[el.element]?.response;
                                const rawSummary = bbInsight || el.aiSummary || '';
                                const aiSummary = rawSummary.replace(/^#[^\n]*\n\s*/, '').trim();
                                const isInsightLoading = buildingBlocksLoading && !bbInsight;
                                const score = el.score != null ? Math.round(el.score) : null;
                                // Color accent matching orbital visualization rings
                                const orbitalColorMap: Record<string, string> = {
                                    'Behavioural Loyalty': '#8fb8d4',
                                    'Attitudinal Loyalty': '#8fb8d4',
                                    'Market Awareness': '#b39ddb',
                                    'Associations': '#8fb8d4',
                                    'Alignment Depth': '#e07a5f',
                                    'Willingness to Pay a Premium': '#8fb8d4',
                                    'Brand Attachment': '#e07a5f',
                                    'Quality Value Tradeoff (PQV)': '#e07a5f',
                                };
                                const accentColor = isLocked ? '#d6d3d1' : (orbitalColorMap[el.element] || '#8fb8d4');

                                return (
                                    <Col xs={24} md={12} key={el.element} style={{ display: 'flex' }}>
                                        <div
                                            onClick={() => navigate(`/dashboard/analytics/visualization?element=${encodeURIComponent(el.element)}`)}
                                            style={{
                                                position: 'relative',
                                                padding: '16px 18px 16px 22px',
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                transition: 'all 0.25s ease',
                                                background: isLocked ? '#fafaf9' : '#fff',
                                                opacity: isLocked ? 0.85 : 1,
                                                border: '1px solid #f0ebe3',
                                                borderLeft: `4px solid ${accentColor}`,
                                                overflow: 'hidden',
                                                flex: 1,
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.boxShadow = `0 4px 16px ${accentColor}18, 0 1px 4px rgba(0,0,0,0.06)`;
                                                e.currentTarget.style.borderColor = `${accentColor}40`;
                                                e.currentTarget.style.borderLeftColor = accentColor;
                                                e.currentTarget.style.transform = 'translateY(-1px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.boxShadow = 'none';
                                                e.currentTarget.style.borderColor = '#f0ebe3';
                                                e.currentTarget.style.borderLeftColor = accentColor;
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            {/* Top row: element label + score */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: '6px',
                                            }}>
                                                <span style={{
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    color: '#78716c',
                                                    textTransform: 'uppercase' as const,
                                                    letterSpacing: '0.06em',
                                                }}>
                                                    {getDisplayName(el.element)}
                                                </span>
                                                {!isLocked && (
                                                    <span style={{
                                                        fontSize: '18px',
                                                        fontWeight: 800,
                                                        color: accentColor,
                                                        lineHeight: 1,
                                                    }}>
                                                        {score != null ? `${score}%` : 'N/A'}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Core question as headline */}
                                            {coreQuestion && (
                                                <div style={{
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    color: isLocked ? '#78716c' : '#1c1917',
                                                    lineHeight: '1.35',
                                                    marginBottom: '8px',
                                                }}>
                                                    {coreQuestion}
                                                </div>
                                            )}

                                            {/* Locked: skeleton + upgrade teaser */}
                                            {isLocked && (
                                                <div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
                                                        <div style={{ height: '10px', borderRadius: '4px', background: '#e7e5e4', width: '100%' }} />
                                                        <div style={{ height: '10px', borderRadius: '4px', background: '#e7e5e4', width: '75%' }} />
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '5px',
                                                        fontSize: '11px',
                                                        fontWeight: 600,
                                                        color: '#F59E0B',
                                                    }}>
                                                        <LockOutlined style={{ fontSize: '11px' }} />
                                                        Upgrade to unlock insight
                                                    </div>
                                                </div>
                                            )}

                                            {/* AI insight — prominent display */}
                                            {!isLocked && isInsightLoading && (
                                                <div style={{
                                                    fontSize: '12.5px',
                                                    color: '#c4b5a4',
                                                    lineHeight: '1.5',
                                                }}>
                                                    <LoadingOutlined style={{ marginRight: 6 }} />
                                                    Generating insight...
                                                </div>
                                            )}
                                            {aiSummary && !isLocked && !isInsightLoading && (
                                                <div style={{
                                                    fontSize: '12.5px',
                                                    color: '#57534e',
                                                    lineHeight: '1.55',
                                                    letterSpacing: '-0.01em',
                                                }}>
                                                    {aiSummary}
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                        </div>
                    </div>
                );
            })()}

            {/* CTA title + Compact AI Expert Insights */}
            {brandEquityData && !brandEquityData.insufficientSampleSize && aiExpertsData && aiExpertsData.length > 0 && (() => {
                const colorMap: { [key: string]: { bg: string, light: string } } = {
                    'bg-blue-500': { bg: '#3b82f6', light: '#eff6ff' },
                    'bg-purple-500': { bg: '#8b5cf6', light: '#f5f3ff' },
                    'bg-green-500': { bg: '#10b981', light: '#ecfdf5' },
                    'bg-orange-500': { bg: '#f97316', light: '#fff7ed' },
                    'bg-red-500': { bg: '#ef4444', light: '#fef2f2' },
                    'bg-pink-500': { bg: '#ec4899', light: '#fdf2f8' },
                    'bg-indigo-500': { bg: '#6366f1', light: '#eef2ff' },
                    'bg-yellow-500': { bg: '#eab308', light: '#fefce8' },
                };
                const iconMap: { [key: string]: React.ReactNode } = {
                    BookOpen: <BookOutlined />,
                    GraduationCap: <BookOutlined />,
                    Megaphone: <MessageOutlined />,
                    Brain: <BarChartOutlined />,
                    Target: <BarChartOutlined />,
                    ChartBar: <BarChartOutlined />,
                    Briefcase: <BarChartOutlined />,
                };

                const activeExperts = aiExpertsData.filter(e => e.isActive);
                if (activeExperts.length === 0) return null;

                return (
                    <div style={{ marginBottom: '24px' }}>
                        {/* CTA title with animated arrows */}
                        <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                            {/* Left arrows */}
                            <span style={{ display: 'inline-flex', gap: '4px' }}>
                                {[0, 1, 2].map(i => (
                                    <span
                                        key={`l${i}`}
                                        style={{
                                            display: 'inline-block',
                                            fontSize: 30,
                                            color: '#F59E0B',
                                            fontWeight: 700,
                                            opacity: 0.3 + i * 0.3,
                                            animation: `ctaArrowRight 1.5s ease-in-out ${i * 0.15}s infinite`,
                                        }}
                                    >
                                        &#x203A;
                                    </span>
                                ))}
                            </span>

                            <span style={{
                                fontSize: 28,
                                fontWeight: 500,
                                color: '#57534e',
                                letterSpacing: '-0.01em',
                                cursor: 'pointer',
                            }}
                                onClick={() => navigate('/dashboard/advice#tabs')}
                            >
                                This is a great place to{' '}
                                <span style={{
                                    color: '#92400E',
                                    fontWeight: 700,
                                    textDecoration: 'underline',
                                    textDecorationColor: '#F59E0B',
                                    textUnderlineOffset: '4px',
                                    textDecorationThickness: '2px',
                                    transition: 'color 0.15s',
                                }}>
                                    start
                                </span>
                            </span>

                            {/* Right arrows */}
                            <span style={{ display: 'inline-flex', gap: '4px' }}>
                                {[0, 1, 2].map(i => (
                                    <span
                                        key={`r${i}`}
                                        style={{
                                            display: 'inline-block',
                                            fontSize: 30,
                                            color: '#F59E0B',
                                            fontWeight: 700,
                                            opacity: 0.9 - i * 0.3,
                                            animation: `ctaArrowLeft 1.5s ease-in-out ${i * 0.15}s infinite`,
                                        }}
                                    >
                                        &#x2039;
                                    </span>
                                ))}
                            </span>

                            {/* Keyframe animations injected via style tag */}
                            <style>{`
                                @keyframes ctaArrowRight {
                                    0%, 100% { transform: translateX(-5px); opacity: 0.3; }
                                    50% { transform: translateX(5px); opacity: 1; }
                                }
                                @keyframes ctaArrowLeft {
                                    0%, 100% { transform: translateX(5px); opacity: 0.3; }
                                    50% { transform: translateX(-5px); opacity: 1; }
                                }
                            `}</style>
                        </div>

                        {/* Section label */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '14px',
                        }}>
                            <ExperimentOutlined style={{ fontSize: 14, color: '#F59E0B' }} />
                            <span style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#a16207',
                                textTransform: 'uppercase',
                                letterSpacing: '1.2px',
                            }}>
                                Real-time analysis from our resident AI Experts
                            </span>
                        </div>

                        {/* Expert cards in 3-column grid */}
                        <Row gutter={[16, 16]}>
                            {activeExperts.map((expert) => {
                                const colors = colorMap[expert.color] || { bg: '#3b82f6', light: '#eff6ff' };
                                const icon = iconMap[expert.icon] || <BookOutlined />;
                                const insight = currentInsights?.[expert.expertType];
                                const isInsightLoading = (insightsLoading && brandEquityData) || loadingExperts.has(expert.expertType);

                                // Get insight text, preserving bold.
                                //
                                // Escape FIRST: this is AI output summarising survey data, and
                                // survey open-text answers come from anonymous respondents. None
                                // of the replacements below escape anything, so without this a
                                // respondent could write markup that executes in an org admin's
                                // session. Sanitising after is a second layer in case a future
                                // replacement emits a tag of its own.
                                let insightHtml = '';
                                if (insight?.response) {
                                    insightHtml = sanitizeInlineHtml(
                                        escapeHtml(insight.response)
                                            .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
                                            .replace(/#{1,3}\s+/g, '')
                                            .replace(/\n/g, ' ')
                                            .replace(/[•\-]\s+/g, ' ')
                                            .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#374151">$1</strong>')
                                            .replace(/\s{2,}/g, ' ')
                                            .trim()
                                    );
                                }

                                return (
                                    <Col xs={24} md={8} key={expert.expertType}>
                                        <div
                                            onClick={() => {
                                                const prompt = insight?.response
                                                    ? `Based on your earlier insight: "${insight.response.replace(/\*\*/g, '').substring(0, 300)}"\n\nCan you elaborate on this analysis and what actions we should consider?`
                                                    : undefined;
                                                const params = new URLSearchParams({ expert: expert.expertType });
                                                if (prompt) params.set('prompt', prompt);
                                                navigate(`/dashboard/ai-experts?${params.toString()}`);
                                            }}
                                            style={{
                                                background: '#ffffff',
                                                borderRadius: '16px',
                                                padding: '20px',
                                                height: '100%',
                                                border: `1px solid ${colors.bg}`,
                                                boxShadow: `0 2px 8px ${colors.bg}14`,
                                                cursor: 'pointer',
                                                transition: 'box-shadow 0.2s',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.boxShadow = `0 6px 24px ${colors.bg}20`;
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.boxShadow = `0 2px 8px ${colors.bg}14`;
                                            }}
                                        >
                                            {/* Header: icon + name + refresh */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                                                <div style={{
                                                    width: 36, height: 36, borderRadius: '10px',
                                                    background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg}dd)`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                    boxShadow: `0 2px 8px ${colors.bg}4d`,
                                                }}>
                                                    {React.cloneElement(icon as React.ReactElement, {
                                                        style: { fontSize: 18, color: 'white' }
                                                    })}
                                                </div>
                                                <Text strong style={{ fontSize: 15, color: '#1f2937', letterSpacing: '-0.01em', flex: 1 }}>
                                                    {expert.name}
                                                </Text>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={isInsightLoading ? <LoadingOutlined /> : <ReloadOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        refreshSingleExpert(expert.expertType);
                                                    }}
                                                    loading={!!isInsightLoading}
                                                    style={{
                                                        padding: '2px 6px',
                                                        height: 24,
                                                        minWidth: 24,
                                                        color: '#d6d3d1',
                                                        flexShrink: 0,
                                                    }}
                                                    title="Refresh analysis"
                                                />
                                            </div>

                                            {/* Insight content */}
                                            {isInsightLoading ? (
                                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px 0' }}>
                                                    <Text style={{ fontSize: 13, color: '#9ca3af' }}>
                                                        <LoadingOutlined style={{ marginRight: 6 }} />Analyzing...
                                                    </Text>
                                                </div>
                                            ) : insightHtml ? (
                                                <div
                                                    style={{
                                                        fontSize: 13,
                                                        color: '#78716c',
                                                        lineHeight: '1.6',
                                                        flex: 1,
                                                        overflow: 'hidden',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: insightHtml }}
                                                />
                                            ) : (
                                                <Text style={{ fontSize: 13, color: '#d6d3d1', fontStyle: 'italic', flex: 1 }}>
                                                    {expert.specialty}
                                                </Text>
                                            )}
                                        </div>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>
                );
            })()}

            {/* Deep-dive CTA linking to AI Advice */}
            {brandEquityData && !brandEquityData.insufficientSampleSize && (
                <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                    {/* Left arrows */}
                    <span style={{ display: 'inline-flex', gap: '4px' }}>
                        {[0, 1, 2].map(i => (
                            <span
                                key={`dl${i}`}
                                style={{
                                    display: 'inline-block',
                                    fontSize: 30,
                                    color: '#F59E0B',
                                    fontWeight: 700,
                                    opacity: 0.3 + i * 0.3,
                                    animation: `ctaArrowRight 1.5s ease-in-out ${i * 0.15}s infinite`,
                                }}
                            >
                                &#x203A;
                            </span>
                        ))}
                    </span>

                    <span style={{
                        fontSize: 28,
                        fontWeight: 500,
                        color: '#57534e',
                        letterSpacing: '-0.01em',
                        cursor: 'pointer',
                    }}
                        onClick={() => navigate('/dashboard/advice?tab=opportunities')}
                    >
                        Explore <span style={{ color: '#92400E', fontWeight: 700 }}>{firstBrand?.name || 'Brand'}'s</span>{' '}
                        <a
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/dashboard/advice?tab=opportunities');
                            }}
                            href="/dashboard/advice?tab=opportunities"
                            style={{
                                color: '#92400E',
                                fontWeight: 700,
                                textDecoration: 'underline',
                                textDecorationColor: '#F59E0B',
                                textUnderlineOffset: '4px',
                                textDecorationThickness: '2px',
                                transition: 'color 0.15s',
                            }}
                            onMouseEnter={(e) => { (e.target as HTMLElement).style.color = '#78350F'; }}
                            onMouseLeave={(e) => { (e.target as HTMLElement).style.color = '#92400E'; }}
                        >
                            opportunities
                        </a>
                    </span>

                    {/* Right arrows */}
                    <span style={{ display: 'inline-flex', gap: '4px' }}>
                        {[0, 1, 2].map(i => (
                            <span
                                key={`dr${i}`}
                                style={{
                                    display: 'inline-block',
                                    fontSize: 30,
                                    color: '#F59E0B',
                                    fontWeight: 700,
                                    opacity: 0.9 - i * 0.3,
                                    animation: `ctaArrowLeft 1.5s ease-in-out ${i * 0.15}s infinite`,
                                }}
                            >
                                &#x2039;
                            </span>
                        ))}
                    </span>
                </div>
            )}

            {/* Navigation Pillars — Intelligence, Results, Trends */}
            {brandEquityData && !brandEquityData.insufficientSampleSize && (
                <Row gutter={[20, 20]} style={{ marginBottom: '24px' }}>
                    {/* Intelligence */}
                    <Col xs={24} md={8}>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '24px',
                            height: '100%',
                            border: '1px solid #f0ebe3',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.03)';
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(245,158,11,0.3)',
                                }}>
                                    <BulbOutlined style={{ fontSize: 18, color: 'white' }} />
                                </div>
                                <Text strong style={{ fontSize: 17, color: '#44403c', letterSpacing: '-0.01em' }}>
                                    Intelligence
                                </Text>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    { text: 'Get an AI-powered summary of your brand performance', path: '/dashboard/advice#tabs' },
                                    { text: 'New here? Start with personalised advice for your brand', path: '/dashboard/advice#tabs' },
                                    { text: `Chat to AI Experts about ${firstBrand?.name || 'Brand'}'s data`, path: '/dashboard/ai-experts' },
                                ].map((item, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '8px 10px', borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s',
                                    }}
                                        onClick={() => navigate(item.path)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(0,0,0,0.03)';
                                            const textEl = e.currentTarget.querySelector('.link-text') as HTMLElement;
                                            if (textEl) textEl.style.color = '#1c1917';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            const textEl = e.currentTarget.querySelector('.link-text') as HTMLElement;
                                            if (textEl) textEl.style.color = '#78716c';
                                        }}
                                    >
                                        <RightOutlined style={{ fontSize: 10, color: '#a8a29e', flexShrink: 0 }} />
                                        <Text className="link-text" style={{ fontSize: 14, color: '#78716c', lineHeight: '1.5', transition: 'color 0.15s' }}>
                                            {item.text}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Col>

                    {/* Results */}
                    <Col xs={24} md={8}>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '24px',
                            height: '100%',
                            border: '1px solid #f0ebe3',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.03)';
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #059669, #047857)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(5,150,105,0.3)',
                                }}>
                                    <BarChartOutlined style={{ fontSize: 18, color: 'white' }} />
                                </div>
                                <Text strong style={{ fontSize: 17, color: '#44403c', letterSpacing: '-0.01em' }}>
                                    Results
                                </Text>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    { text: `Explore ${firstBrand?.name || 'Brand'}'s full orbital visualisation`, path: '/dashboard/analytics/visualization' },
                                    { text: 'See how customers responded to each brand statement', path: '/dashboard/analytics/statements' },
                                    { text: 'Dive deeper into specific brand equity elements', path: '/dashboard/advice?tab=explore-further' },
                                ].map((item, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '8px 10px', borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s',
                                    }}
                                        onClick={() => navigate(item.path)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(0,0,0,0.03)';
                                            const textEl = e.currentTarget.querySelector('.link-text') as HTMLElement;
                                            if (textEl) textEl.style.color = '#1c1917';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            const textEl = e.currentTarget.querySelector('.link-text') as HTMLElement;
                                            if (textEl) textEl.style.color = '#78716c';
                                        }}
                                    >
                                        <RightOutlined style={{ fontSize: 10, color: '#a8a29e', flexShrink: 0 }} />
                                        <Text className="link-text" style={{ fontSize: 14, color: '#78716c', lineHeight: '1.5', transition: 'color 0.15s' }}>
                                            {item.text}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Col>

                    {/* Trends */}
                    <Col xs={24} md={8}>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '16px',
                            padding: '24px',
                            height: '100%',
                            border: '1px solid #f0ebe3',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.03)';
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
                                }}>
                                    <LineChartOutlined style={{ fontSize: 18, color: 'white' }} />
                                </div>
                                <Text strong style={{ fontSize: 17, color: '#44403c', letterSpacing: '-0.01em' }}>
                                    Trends
                                </Text>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    { text: `Track ${firstBrand?.name || 'Brand'}'s equity score over time`, path: '/dashboard/analytics/trends' },
                                    { text: 'Compare your performance against industry benchmarks', path: '/dashboard/benchmarking' },
                                    { text: 'Spot notable shifts and emerging patterns in your data', path: '/dashboard/advice?tab=notable-changes' },
                                ].map((item, i) => (
                                    <div key={i} style={{
                                        display: 'flex', alignItems: 'center', gap: 8,
                                        padding: '8px 10px', borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'background 0.15s',
                                    }}
                                        onClick={() => navigate(item.path)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(0,0,0,0.03)';
                                            const textEl = e.currentTarget.querySelector('.link-text') as HTMLElement;
                                            if (textEl) textEl.style.color = '#1c1917';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            const textEl = e.currentTarget.querySelector('.link-text') as HTMLElement;
                                            if (textEl) textEl.style.color = '#78716c';
                                        }}
                                    >
                                        <RightOutlined style={{ fontSize: 10, color: '#a8a29e', flexShrink: 0 }} />
                                        <Text className="link-text" style={{ fontSize: 14, color: '#78716c', lineHeight: '1.5', transition: 'color 0.15s' }}>
                                            {item.text}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Col>
                </Row>
            )}

            {/* Old dashboard sections (Dashboard Header + Expert Analytics Hub) removed — replaced by new hero + pillars above */}

        </div>
    );
}

export default DashboardPage;
